import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Hash, Calendar, User, Wrench, type LucideIcon } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Spinner } from "@/components/ui";
import { AssetPhotoGallery } from "@/features/assets/AssetPhotoGallery";

// =============================================================================
// HandToolDetailScreen — /assets/hand-tools/:toolId
// Lightweight read-only view of a hand tool. Coretel does NOT test-and-tag
// hand tools, so no inspection history panel. Identity + lifecycle +
// assignment only. T-055 Commit 3.
// =============================================================================

interface HandToolFull {
  tool_id:           string;
  tool_code:         string;
  display_name:      string;
  tool_category:     string | null;
  tool_type:         string | null;
  size_spec:         string | null;
  make:              string | null;
  model:             string | null;
  serial_number:     string | null;
  asset_tag:         string | null;
  batch_lot_number:  string | null;
  quantity:          number;
  purchase_date:     string | null;
  purchase_price:    number | null;
  purchase_supplier: string | null;
  condition:         string | null;
  is_active:         boolean;
  kit_name:          string | null;
  assigned_to_employee_id: string | null;
  assigned_from:     string | null;
  notes:             string | null;
  photo_url:         string | null;
  assigned_to?: { first_name: string | null; last_name: string | null } | null;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${dt.getDate().toString().padStart(2, "0")}/${(dt.getMonth() + 1).toString().padStart(2, "0")}/${dt.getFullYear()}`;
  } catch { return d; }
}

function fullName(e: { first_name: string | null; last_name: string | null } | null | undefined): string | null {
  if (!e) return null;
  const n = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
  return n || null;
}

// -----------------------------------------------------------------------------

export default function HandToolDetailScreen() {
  const { toolId } = useParams<{ toolId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  const canEdit = user?.role === "director" || user?.role === "manager";

  const { data: item, isLoading, error } = useQuery({
    queryKey: ["hand_tool_detail", toolId],
    enabled: !!toolId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hand_tools")
        .select(`
          tool_id, tool_code, display_name, tool_category, tool_type,
          size_spec, make, model, serial_number, asset_tag,
          batch_lot_number, quantity,
          purchase_date, purchase_price, purchase_supplier,
          condition, is_active, kit_name,
          assigned_to_employee_id, assigned_from, notes, photo_url,
          assigned_to:employees!hand_tools_assigned_to_employee_id_fkey(first_name, last_name)
        `)
        .eq("tool_id", toolId!)
        .single();
      if (error) throw error;
      return data as unknown as HandToolFull;
    },
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (error || !item) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-[13px] text-red-400">
            Hand tool not found{error ? `: ${error instanceof Error ? error.message : String(error)}` : "."}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto pb-8">
      <button onClick={() => navigate("/assets")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Back to assets
      </button>

      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-wide text-text-muted font-mono">{item.tool_code}</div>
        <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mt-0.5">
          <Wrench size={20} className="text-accent" />
          {item.display_name}
        </h1>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[12px] text-text-muted">
          {item.tool_category && <Pill label={item.tool_category} tone="notice" small />}
          {item.tool_type && <span>{item.tool_type}</span>}
          {item.size_spec && <span>· {item.size_spec}</span>}
          {item.quantity > 1 && <span>· qty {item.quantity}</span>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="p-5">
            <SectionLabel icon={Hash}>Identity</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <RO label="Asset tag" value={item.asset_tag} mono />
              <RO label="Category" value={item.tool_category} />
              <RO label="Type" value={item.tool_type} />
              <RO label="Size / spec" value={item.size_spec} />
              <RO label="Make" value={item.make} />
              <RO label="Model" value={item.model} />
              <RO label="Serial" value={item.serial_number} mono />
              <RO label="Batch / lot" value={item.batch_lot_number} mono />
            </dl>
          </div>
        </Card>

        <Card>
          <div className="p-5">
            <SectionLabel icon={Calendar}>Lifecycle</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <RO label="Purchase date" value={fmtDate(item.purchase_date)} />
              <RO label="Price" value={item.purchase_price != null ? `$${item.purchase_price.toFixed(2)}` : null} mono />
              <RO label="Supplier" value={item.purchase_supplier} />
              <RO label="Quantity" value={item.quantity ?? 1} />
              <RO label="Condition" value={item.condition} />
              <RO label="Active" value={item.is_active ? "Yes" : "No"} />
            </dl>
          </div>
        </Card>
      </div>

      <Card>
        <div className="p-5 mt-4">
          <SectionLabel icon={User}>Assignment</SectionLabel>
          <dl className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-[13px]">
            <RO label="Assigned to" value={fullName(item.assigned_to) ?? "Pool"} />
            <RO label="Assigned from" value={fmtDate(item.assigned_from)} />
            <RO label="Kit / allocation" value={item.kit_name} />
          </dl>
        </div>
      </Card>

      {item.notes && !item.notes.startsWith("[imported") && (
        <Card>
          <div className="p-5 mt-4">
            <SectionLabel>Notes</SectionLabel>
            <div className="mt-2 text-[13px] text-text whitespace-pre-wrap">{item.notes}</div>
          </div>
        </Card>
      )}

      <div className="mt-6">
        <AssetPhotoGallery assetKind="hand_tool" assetId={item.tool_id} canEdit={canEdit} />
      </div>

      <div className="mt-6 text-[11px] text-text-muted italic">
        Hand tools are not on the test-and-tag schedule. Use the condition field to flag wear.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function SectionLabel({ icon: Icon, children }: { icon?: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider2 text-text-muted font-medium">
      {Icon && <Icon size={12} />}
      {children}
    </div>
  );
}

function RO({ label, value, mono = false }: { label: string; value: string | number | null | undefined; mono?: boolean }) {
  return (
    <>
      <dt className="text-text-muted">{label}</dt>
      <dd className={`text-text ${mono ? "font-mono text-[12px]" : ""}`}>
        {value === null || value === undefined || value === "" ? <span className="text-text-dim">—</span> : value}
      </dd>
    </>
  );
}
